import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  Terminal,
  LocationOn,
  Upload,
  People,
  AccountCircle,
  Logout,
  PersonOff,
  CloudUpload,
  Computer,
  CreditCard,
  Assessment,
  Language,
  OpenInNew,
  BugReport,
  HelpOutline,
  ExpandMore,
  ExpandLess,
  Search,
  DateRange,
  FileDownload,
  Visibility,
  Info,
  Inventory,
  Category,
  Schedule,
  Payments,
  AssignmentInd,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../contexts/ApiContext';
import TranslatorUILink from './TranslatorUILink';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [expandedHelp, setExpandedHelp] = useState(false);
  const loginRecorded = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, signOut, hasPermission, loading, refreshProfile } = useAuth();
  const { recordLogin } = useApi();

  // Record user login once when authenticated
  useEffect(() => {
    if (userProfile && !userProfile.isImpersonated && !loginRecorded.current) {
      loginRecorded.current = true;
      recordLogin().catch(err => {
        console.warn('Failed to record login:', err);
      });
    }
  }, [userProfile, recordLogin]);

  const handleHelpOpen = () => setHelpOpen(true);
  const handleHelpClose = () => {
    setHelpOpen(false);
    setExpandedHelp(false);
  };
  const handleHelpAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedHelp(isExpanded ? panel : false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = () => {
    signOut();
    handleClose();
  };

  const handleStopImpersonation = () => {
    // Clear impersonation data from sessionStorage
    sessionStorage.removeItem('impersonationToken');
    sessionStorage.removeItem('impersonatedUser');
    sessionStorage.removeItem('isImpersonating');
    
    // Refresh the user profile to return to original admin user
    if (refreshProfile) {
      refreshProfile();
    }
    
    // Navigate to overview page
    navigate('/overview');
  };

  const menuItems = [
    {
      text: 'Overview',
      icon: <Dashboard />,
      path: '/overview',
      permission: 'overview'
    },
    {
      text: 'Terminals',
      icon: <Terminal />,
      path: '/terminals',
      permission: 'terminals'
    },
    {
      text: 'Settlement Reports',
      icon: <Assessment />,
      path: '/settlement-reports',
      permission: 'overview'  // All users with overview access can see this
    },
    {
      text: 'Scheduled Reports',
      icon: <Schedule />,
      path: '/scheduled-reports',
      permission: 'overview'  // All authenticated users can schedule reports
    },
    {
      text: 'Partner Surcharge',
      icon: <Payments />,
      path: '/partner-surcharge',
      permission: 'partner_surcharge'
    },
    // group: 'admin' items are collapsed under the "Administration" dropdown in the drawer.
    // All of these are admin-gated (admin_only / sftp_management / manage_pai_accounts all
    // resolve to admins in hasPermission); user_management additionally allows delegated
    // managers (users.can_manage_users), who will see the group containing just that item.
    {
      text: 'Upload Data',
      icon: <Upload />,
      path: '/upload',
      permission: 'admin_only',
      group: 'admin'
    },
    {
      text: 'User Management',
      icon: <People />,
      path: '/users',
      permission: 'user_management',
      group: 'admin'
    },
    {
      text: 'Onboarding',
      icon: <AssignmentInd />,
      path: '/onboarding',
      permission: 'admin_only',
      group: 'admin'
    },
    {
      text: 'SFTP Management',
      icon: <CloudUpload />,
      path: '/sftp',
      permission: 'sftp_management',
      group: 'admin'
    },
    {
      text: 'ATM Servers',
      icon: <Computer />,
      path: '/atm-servers',
      permission: 'admin_only',
      group: 'admin'
    },
    {
      text: 'Channels',
      icon: <Category />,
      path: '/channels',
      permission: 'admin_only',
      group: 'admin'
    },
    {
      text: 'Settlement Dashboard',
      icon: <CreditCard />,
      path: '/pai',
      permission: 'manage_pai_accounts',
      group: 'admin'
    },
    {
      text: 'Troubleshooting',
      icon: <BugReport />,
      path: '/troubleshooting',
      permission: 'terminals'  // Available for all users with terminal access
    }
  ];

  const adminItems = menuItems.filter((i) => i.group === 'admin');
  const mainItems = menuItems.filter((i) => i.group !== 'admin');
  // Only render the Administration dropdown if the user can actually see something in it
  const showAdminGroup = adminItems.some((i) => hasPermission(i.permission));
  // Start expanded when the user is already on one of the Administration pages, so the
  // active page is never hidden inside a collapsed group.
  const [adminOpen, setAdminOpen] = useState(() =>
    adminItems.some((i) => location.pathname.startsWith(i.path))
  );

  const renderNavItem = (item, sx = {}) => (
    <ListItem key={item.text} disablePadding>
      <ListItemButton
        sx={sx}
        selected={location.pathname === item.path || (item.path === '/onboarding' && location.pathname.startsWith('/onboarding/'))}
        onClick={() => {
          // If clicking on terminals while already there, force a refresh
          if (item.path === '/terminals' && location.pathname === '/terminals') {
            navigate('/terminals', { replace: true, state: { clearSearch: true } });
          } else {
            navigate(item.path);
          }
          setMobileOpen(false);
        }}
      >
        {/* <ListItemIcon>{item.icon}</ListItemIcon> */}
        <ListItemText
          primary={item.text}
          primaryTypographyProps={{
            sx: { fontFamily: 'Lato, sans-serif' }
          }}
        />
      </ListItemButton>
    </ListItem>
  );

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Reporting Portal
        </Typography>
      </Toolbar>
      <Divider />
      {userProfile && (
        <>
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Welcome, {userProfile.firstName} {userProfile.lastName}
            </Typography>
            <Chip 
              label={userProfile.role.toUpperCase()} 
              size="small" 
              color={userProfile.role === 'admin' ? 'error' : userProfile.role === 'manager' ? 'warning' : 'default'}
              sx={{ mt: 1 }}
            />
            {userProfile.isImpersonated && (
              <Alert severity="warning" sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2">
                  Impersonating {userProfile.firstName} {userProfile.lastName}
                </Typography>
              </Alert>
            )}
          </Box>
          <Divider />
        </>
      )}
      
      {/* Stop Impersonation Button - Only show during impersonation */}
      {userProfile && userProfile.isImpersonated && (
        <>
          <List>
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleStopImpersonation}
                sx={{
                  backgroundColor: 'warning.light',
                  '&:hover': { backgroundColor: 'warning.main' }
                }}
              >
                {/* <ListItemIcon>
                  <PersonOff color="warning" />
                </ListItemIcon> */}
                <ListItemText
                  primary="Stop Impersonation"
                  primaryTypographyProps={{
                    fontWeight: 'bold',
                    sx: { fontFamily: 'Lato, sans-serif' }
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
          <Divider />
        </>
      )}
      <List>
        {mainItems.map((item) => {
          if (!hasPermission(item.permission)) {
            return null;
          }

          return renderNavItem(item);
        })}

        {/* Administration — admin-gated pages collapsed into one dropdown to keep the
            main navigation short. Rendered only when the user can see at least one. */}
        {showAdminGroup && (
          <>
            <ListItem disablePadding>
              <ListItemButton onClick={() => setAdminOpen(!adminOpen)}>
                <ListItemText
                  primary="Administration"
                  primaryTypographyProps={{
                    sx: { fontFamily: 'Lato, sans-serif', fontWeight: 600 }
                  }}
                />
                {adminOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </ListItemButton>
            </ListItem>
            <Collapse in={adminOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {adminItems.map((item) => {
                  if (!hasPermission(item.permission)) {
                    return null;
                  }

                  return renderNavItem(item, { pl: 4 });
                })}
              </List>
            </Collapse>
          </>
        )}
      </List>
      
      {/* Add spacer to push Translator UI to bottom */}
      <Box sx={{ flexGrow: 1 }} />
      
      {/* Translator UI Link - Secure OAuth2 Integration - Admin only */}
      {userProfile && userProfile.role === 'admin' && (
        <>
          <Divider />
          <List>
            <TranslatorUILink />
          </List>
        </>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: 'Allura, cursive',
                fontSize: '2.5rem',
                letterSpacing: '2px',
                fontWeight: 400,
                lineHeight: 1,
                color: 'inherit'
              }}
            >
              MyView
            </Typography>
            <Typography variant="h6" noWrap component="div">
              Reporting Portal
            </Typography>
          </Box>
          {userProfile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Help & Documentation">
                <IconButton
                  size="large"
                  aria-label="help"
                  onClick={handleHelpOpen}
                  color="inherit"
                  sx={{ mr: 1 }}
                >
                  <HelpOutline />
                </IconButton>
              </Tooltip>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32 }}>
                  {userProfile.firstName?.[0]}{userProfile.lastName?.[0]}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={() => {
                  handleClose();
                  navigate('/profile');
                }}>
                  <AccountCircle sx={{ mr: 1 }} />
                  Profile
                </MenuItem>
                <MenuItem onClick={handleSignOut}>
                  <Logout sx={{ mr: 1 }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8
        }}
      >
        {userProfile?.role === 'viewer' && (userProfile.allowedTerminals.length === 0 && userProfile.allowedLocations.length === 0) && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your account has limited access. Please contact an administrator to configure your permissions.
          </Alert>
        )}
        {children}
      </Box>

      {/* Help Documentation Dialog */}
      <Dialog
        open={helpOpen}
        onClose={handleHelpClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutline color="primary" />
          <Typography variant="h6">MyView Reporting Portal Help</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" paragraph>
            Welcome to the MyView Reporting Portal. Below you'll find guides for each section of the portal.
          </Typography>

          {/* Overview Section */}
          <Accordion
            expanded={expandedHelp === 'overview'}
            onChange={handleHelpAccordionChange('overview')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Dashboard color="primary" fontSize="small" />
                <Typography fontWeight="medium">Overview</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                The <strong>Overview</strong> page provides a summary of your transaction activity across all your terminals.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Features:</strong>
                <ul>
                  <li><strong>Date Range Selection:</strong> Choose a date range to view transaction summaries</li>
                  <li><strong>Summary Cards:</strong> View total transactions, approved transactions, and total amounts</li>
                  <li><strong>Transaction Table:</strong> Detailed breakdown by terminal showing transaction counts and amounts</li>
                  <li><strong>Export:</strong> Download your data as a CSV file for record keeping</li>
                </ul>
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Terminals Section */}
          <Accordion
            expanded={expandedHelp === 'terminals'}
            onChange={handleHelpAccordionChange('terminals')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Terminal color="primary" fontSize="small" />
                <Typography fontWeight="medium">Terminals</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                The <strong>Terminals</strong> page allows you to view detailed transaction history for individual ATM terminals.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>How to Use:</strong>
                <ul>
                  <li><strong>Search:</strong> Enter a terminal ID in the search box to find a specific terminal</li>
                  <li><strong>Date Selection:</strong> Choose a date to view transactions for that day</li>
                  <li><strong>Transaction Details:</strong> View each transaction including time, amount, card type, and status</li>
                  <li><strong>Status Indicators:</strong> Green indicates approved transactions, red indicates declined</li>
                </ul>
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Locations Section */}
          <Accordion
            expanded={expandedHelp === 'locations'}
            onChange={handleHelpAccordionChange('locations')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn color="primary" fontSize="small" />
                <Typography fontWeight="medium">Locations</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                The <strong>Locations</strong> page shows transaction summaries grouped by your business locations.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Features:</strong>
                <ul>
                  <li><strong>Location List:</strong> View all locations assigned to your account</li>
                  <li><strong>Quick Stats:</strong> See transaction counts and totals per location</li>
                  <li><strong>Terminal Breakdown:</strong> Click on a location to see individual terminal performance</li>
                </ul>
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Settlement Reports Section */}
          <Accordion
            expanded={expandedHelp === 'settlement'}
            onChange={handleHelpAccordionChange('settlement')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Assessment color="primary" fontSize="small" />
                <Typography fontWeight="medium">Settlement Reports</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                The <strong>Settlement Reports</strong> page shows your daily settlement and deposit information.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Features:</strong>
                <ul>
                  <li><strong>Date Range:</strong> Select a date range to view settlement records</li>
                  <li><strong>Location Filter:</strong> Filter by specific locations</li>
                  <li><strong>Settlement Details:</strong> View deposit amounts, transaction counts, and bank routing information</li>
                  <li><strong>Export ACH:</strong> Export settlement data in ACH format for accounting purposes</li>
                </ul>
              </Typography>
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  Settlement reports show the amounts deposited to your bank account, which may differ from gross transaction totals due to fees.
                </Typography>
              </Alert>
            </AccordionDetails>
          </Accordion>

          {/* Troubleshooting Section */}
          <Accordion
            expanded={expandedHelp === 'troubleshooting'}
            onChange={handleHelpAccordionChange('troubleshooting')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BugReport color="primary" fontSize="small" />
                <Typography fontWeight="medium">Troubleshooting</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                The <strong>Troubleshooting</strong> page helps you find terminals that need attention —
                machines with high reversals, card errors, or communication problems. Use the{' '}
                <strong>Overview</strong>, <strong>By Terminal</strong>, and <strong>By Location</strong> tabs
                with the date filters to narrow down when and where issues are happening.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Machine problems vs. bank declines</strong> — not every failed transaction means a
                broken ATM:
                <ul>
                  <li>
                    <strong>Bank declines are NOT machine errors.</strong> Insufficient Funds, Incorrect PIN,
                    Expired Card, and withdrawal-limit declines mean the terminal worked correctly and the
                    cardholder&apos;s bank said no. A terminal whose Top Error is a decline code is usually healthy.
                  </li>
                  <li>
                    <strong>Machine and network problems</strong> are what this page is for: Communication Error
                    (code 6), Suspected Malfunction (22), Key Sync Error (76), Issuer/Switch Inoperative (91),
                    Re-enter Transaction (19), and Invalid Response (20).
                  </li>
                  <li>
                    <strong>Reversals (RWT)</strong> — highlighted in red — mean the customer was approved but did
                    not receive cash (cancelled, or the dispense failed). A rising Reversal Rate is the strongest
                    signal of a dispenser or cash problem.
                  </li>
                  <li>
                    <strong>Partial approvals</strong> (Approved for Partial Amount) can indicate a cassette
                    running low.
                  </li>
                </ul>
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Reading the metrics:</strong>
                <ul>
                  <li>
                    <strong>Total Errors / Failure Rate:</strong> counts ALL non-approved transactions — including
                    bank declines — so always check the Top Error column before assuming a machine issue.
                  </li>
                  <li>
                    <strong>Total Reversals / Reversal Rate:</strong> reversed transactions as a share of the
                    terminal&apos;s total — the key machine-health number.
                  </li>
                  <li>
                    <strong>Error Amount:</strong> the dollar value tied to failed or reversed transactions.
                  </li>
                  <li>
                    <strong>Top Error:</strong> the most frequent response code — this tells you WHY a terminal
                    ranks high.
                  </li>
                </ul>
              </Typography>
              <Typography variant="body2" component="div">
                <strong>How to investigate:</strong>
                <ul>
                  <li>
                    On <strong>By Terminal</strong>, sort by Reversal Rate or Total Reversals to find machines
                    with dispense problems.
                  </li>
                  <li>Repeated Communication Errors → check the terminal&apos;s network connection and power.</li>
                  <li>High reversals or partial approvals → check the cash dispenser and cassette levels.</li>
                  <li>
                    If the Top Error is Insufficient Funds or Incorrect PIN, the machine is fine — that&apos;s
                    normal cardholder activity, not a fault.
                  </li>
                  <li>
                    Use <strong>By Location</strong> to see whether an issue affects one machine or the whole
                    site — site-wide problems usually point to connectivity or power at the location.
                  </li>
                </ul>
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* Profile Section */}
          <Accordion
            expanded={expandedHelp === 'profile'}
            onChange={handleHelpAccordionChange('profile')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccountCircle color="primary" fontSize="small" />
                <Typography fontWeight="medium">Your Profile</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" paragraph>
                Access your <strong>Profile</strong> by clicking on your avatar in the top right corner.
              </Typography>
              <Typography variant="body2" component="div">
                <strong>Profile Options:</strong>
                <ul>
                  <li><strong>View Profile:</strong> See your account information and assigned permissions</li>
                  <li><strong>Sign Out:</strong> Securely log out of your account</li>
                </ul>
              </Typography>
            </AccordionDetails>
          </Accordion>

          {/* FAQ Section */}
          <Accordion
            expanded={expandedHelp === 'faq'}
            onChange={handleHelpAccordionChange('faq')}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info color="primary" fontSize="small" />
                <Typography fontWeight="medium">Frequently Asked Questions</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" component="div">
                <strong>Q: Why don't I see all my terminals?</strong>
                <Typography variant="body2" paragraph sx={{ pl: 2 }}>
                  Your account is configured to show only the terminals assigned to you. Contact your administrator if you believe you should have access to additional terminals.
                </Typography>

                <strong>Q: How often is the data updated?</strong>
                <Typography variant="body2" paragraph sx={{ pl: 2 }}>
                  Transaction data is updated in real-time. Settlement data is typically available the next business day.
                </Typography>

                <strong>Q: What do the different transaction statuses mean?</strong>
                <Typography variant="body2" paragraph sx={{ pl: 2 }}>
                  <strong>Approved:</strong> Transaction completed successfully<br />
                  <strong>Declined:</strong> Transaction was not approved (insufficient funds, invalid card, etc.)<br />
                  <strong>Reversal:</strong> A previously approved transaction was reversed
                </Typography>

                <strong>Q: Who do I contact for support?</strong>
                <Typography variant="body2" sx={{ pl: 2 }}>
                  Contact your account administrator or TFI support team for assistance.
                </Typography>
              </Typography>
            </AccordionDetails>
          </Accordion>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleHelpClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;